import {Accordion, Col, Form, Row, Button, InputGroup, Table} from 'react-bootstrap'
import {TFormKeys} from '../../../../policies/policiesTypes'
import {TFormMemberPolicy} from '../../../../policies/policiesTypes'
import React, {useMemo, useState} from 'react'
import { InputCurrency } from 'app/tfi/components/InputCurrency'
import {copyToClipboard} from '../../../../../../../utils/clipboardUtils'

interface EmploymentIncomeAccordionItemProps {
  eventKey: string
  values: TFormMemberPolicy
  handleChange: (field: string, value: any) => void
  totalIncome?: string
}

export const EmploymentIncomeAccordionItem: React.FC<EmploymentIncomeAccordionItemProps> = ({
  eventKey,
  values,
  handleChange,
  totalIncome,
}) => {
  const [incomeCount, setIncomeCount] = useState(1);
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }),
    []
  )

  const parseIncome = (value?: string | number | null) => {
    if (value === null || value === undefined) return 0
    if (typeof value === 'number') return value
    const cleaned = value.replace(/[^0-9.-]/g, '')
    const parsed = parseFloat(cleaned)
    return Number.isFinite(parsed) ? parsed : 0
  }

  const totalIncomeValue = useMemo(() => {
    const incomes = [values.annualIncome1, values.annualIncome2, values.annualIncome3]
    return incomes.reduce((acc, income) => acc + parseIncome(income), 0)
  }, [values.annualIncome1, values.annualIncome2, values.annualIncome3])

  const totalIncomeDisplay = currencyFormatter.format(totalIncomeValue)

  // Handler to remove income block
  const handleRemoveIncome = (num: 1 | 2 | 3) => {
    const employerNameKey = `employerName${num}` as keyof TFormMemberPolicy
    const selfEmployedKey = `selfEmployed${num}` as keyof TFormMemberPolicy
    const employerPhoneKey = `employerPhone${num}` as keyof TFormMemberPolicy
    const positionOccupationKey = `positionOccupation${num}` as keyof TFormMemberPolicy
    const annualIncomeKey = `annualIncome${num}` as keyof TFormMemberPolicy

    // Special logic: if removing block 2 and block 3 has data, shift block 3's values into block 2, then clear block 3
    if (
      num === 2 &&
      (
        (values['employerName3'] && values['employerName3'] !== '') ||
        Boolean(values['selfEmployed3']) ||
        (values['employerPhone3'] && values['employerPhone3'] !== '') ||
        (values['positionOccupation3'] && values['positionOccupation3'] !== '') ||
        (values['annualIncome3'] && values['annualIncome3'] !== '')
      )
    ) {
      // Shift block 3's values into block 2
      handleChange('employerName2', values['employerName3'] ?? '');
      handleChange('selfEmployed2', Boolean(values['selfEmployed3']));
      handleChange('employerPhone2', values['employerPhone3'] ?? '');
      handleChange('positionOccupation2', values['positionOccupation3'] ?? '');
      handleChange('annualIncome2', values['annualIncome3'] ?? '');
      // Clear block 3
      handleChange('employerName3', '');
      handleChange('selfEmployed3', false);
      handleChange('employerPhone3', '');
      handleChange('positionOccupation3', '');
      handleChange('annualIncome3', '');
      setIncomeCount(incomeCount - 1);
      return;
    }

    // Default: clear the block normally
    handleChange(employerNameKey as string, '')
    handleChange(selfEmployedKey as string, false)
    handleChange(employerPhoneKey as string, '')
    handleChange(positionOccupationKey as string, '')
    handleChange(annualIncomeKey as string, '')

    setIncomeCount(incomeCount - 1);
  }

  // Helper to render each income block as a table row
  const renderIncomeBlock = (num: 1 | 2 | 3) => {
    // Field names for this block
    const employerNameKey = `employerName${num}` as keyof TFormMemberPolicy
    const selfEmployedKey = `selfEmployed${num}` as keyof TFormMemberPolicy
    const employerPhoneKey = `employerPhone${num}` as keyof TFormMemberPolicy
    const positionOccupationKey = `positionOccupation${num}` as keyof TFormMemberPolicy
    const annualIncomeKey = `annualIncome${num}` as keyof TFormMemberPolicy

    const isSelfEmployed = Boolean(values[selfEmployedKey])
    return (
      <tr key={num}>
        <td>
          <Form.Control
            id={`employerName${num}`}
            name={`employerName${num}`}
            type="text"
            placeholder="Employer name"
            value={String(values[employerNameKey] ?? '')}
            disabled={isSelfEmployed}
            onChange={(e) => handleChange(employerNameKey as string, e.target.value)}
          />
        </td>
        <td className="text-center align-middle">
          <Form.Check
            type="checkbox"
            id={`selfEmployed${num}`}
            checked={isSelfEmployed}
            onChange={(e) => {
              const checked = e.target.checked
              handleChange(selfEmployedKey as string, checked)
              if (checked) {
                handleChange(employerNameKey as string, 'Self employed')
              } else {
                handleChange(employerNameKey as string, '')
              }
            }}
          />
        </td>
        <td>
          <Form.Control
            id={`employerPhone${num}`}
            name={`employerPhone${num}`}
            type="text"
            value={String(values[employerPhoneKey] ?? '')}
            onChange={(e) => handleChange(employerPhoneKey as string, e.target.value)}
          />
        </td>
        <td>
          <Form.Control
            id={`positionOccupation${num}`}
            name={`positionOccupation${num}`}
            type="text"
            value={String(values[positionOccupationKey] ?? '')}
            onChange={(e) => handleChange(positionOccupationKey as string, e.target.value)}
          />
        </td>
        <td>
          {num > 1 ? (
            <InputGroup>
              <InputCurrency
                id={`annualIncome${num}`}
                name={`annualIncome${num}`}
                prefix="$"
                className="text-end"
                placeholder="0.00"
                value={String(values[annualIncomeKey] ?? '')}
                onValueChange={(val) => {
                  handleChange(annualIncomeKey as string, val || '0.00')
                }}
              />
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleRemoveIncome(num)}
                className="d-flex align-items-center justify-content-center"
                aria-label="Delete income"
              >
                <i className="bi bi-trash"></i>
              </Button>
            </InputGroup>
          ) : (
            <InputCurrency
              id={`annualIncome${num}`}
              name={`annualIncome${num}`}
              prefix="$"
              className="text-end"
              placeholder="0.00"
              value={String(values[annualIncomeKey] ?? '')}
              onValueChange={(val) => {
                handleChange(annualIncomeKey as string, val || '0.00')
              }}
            />
          )}
        </td>
      </tr>
    )
  }

  // Determine if income blocks 2 and 3 should be shown based on incomeCount or annualIncome value
  const showBlock2 = incomeCount >= 2 || (values.annualIncome2 !== undefined && values.annualIncome2 !== '' && values.annualIncome2 !== '0.00');
  const showBlock3 = incomeCount >= 3 || (values.annualIncome3 !== undefined && values.annualIncome3 !== '' && values.annualIncome3 !== '0.00');

  // Compute effectiveIncomeCount: the highest income field that is non-empty, or incomeCount, whichever is greater
  let effectiveIncomeCount = incomeCount;
  if (values.annualIncome3 !== undefined && values.annualIncome3 !== '' && values.annualIncome3 !== '0.00') {
    effectiveIncomeCount = Math.max(effectiveIncomeCount, 3);
  } else if (values.annualIncome2 !== undefined && values.annualIncome2 !== '' && values.annualIncome2 !== '0.00') {
    effectiveIncomeCount = Math.max(effectiveIncomeCount, 2);
  }

  // Handler for Add Income button
  const handleAddIncome = () => {
    if (incomeCount < 3) {
      setIncomeCount(incomeCount + 1);
    }
  }

  return (
    <Accordion.Item eventKey={eventKey}>
      <Accordion.Header>
        <div style={{ width: '100%' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '2px', fontSize: '0.95rem', lineHeight: '1.2' }}>
            Employment information
          </div>
          <div style={{ fontSize: '0.85rem', color: '#666', display: 'flex', alignItems: 'center', gap: '8px', lineHeight: '1.2' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <i className="bi bi-cash-stack" style={{ fontSize: '0.85rem' }}></i>
              <span>Total income: {totalIncome || totalIncomeDisplay}</span>
            </span>
            <Button
              variant="link"
              size="sm"
              style={{ padding: 0, border: 0, textDecoration: 'none', fontSize: '0.85rem' }}
              title="Copy total income"
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                copyToClipboard(totalIncomeDisplay, 'Total income', totalIncomeValue.toFixed(2))
              }}
            >
              <i className="bi bi-files text-primary"></i>
            </Button>
          </div>
        </div>
      </Accordion.Header>
      <Accordion.Body>
        <Table>
          <thead>
            <tr>
              <th>Employer name</th>
              <th className="text-center">Self employed</th>
              <th>Employer phone</th>
              <th>Position/Occupation</th>
              <th>Annual income</th>
            </tr>
          </thead>
          <tbody>
            {renderIncomeBlock(1)}
            {showBlock2 && renderIncomeBlock(2)}
            {showBlock3 && renderIncomeBlock(3)}
          </tbody>
        </Table>
        {effectiveIncomeCount < 3 && (
          <Button variant="success" onClick={handleAddIncome} size='sm'>
            <i className="bi bi-plus"></i>
            Add Income
          </Button>
        )}
      </Accordion.Body>
    </Accordion.Item>
  )
}
